import { computed, ref, type Ref } from 'vue'
import type { WalletSourceToken } from './contracts'
import { ChainId } from '../domain/flow'
import { formatTokenBalance } from './token-policy'

export interface UseTokenFilterDeps {
  tokens: Ref<WalletSourceToken[]>
  selectedAddress: Ref<string>
  hasConnectedWallet: Ref<boolean>
  chainId: Ref<number | undefined>
  emit: (token: WalletSourceToken) => void
}

export function useTokenFilter(deps: UseTokenFilterDeps) {
  const { tokens, selectedAddress, hasConnectedWallet, chainId, emit } = deps

  const isSolanaChain = computed(() => chainId.value === ChainId.SOLANA)

  const searchQuery = ref('')
  const showOnlyWithBalance = ref(hasConnectedWallet.value && chainId.value !== ChainId.SOLANA)

  const normalizeAddrForCompare = (addr: string) =>
    isSolanaChain.value ? addr : addr.toLowerCase()

  const selectedToken = computed(() => {
    const normalizedSelected = selectedAddress.value
      ? normalizeAddrForCompare(selectedAddress.value)
      : ''
    return tokens.value.find((t) => normalizeAddrForCompare(t.address) === normalizedSelected)
  })

  const matchesBalanceFilter = (token: WalletSourceToken) =>
    token.hasBalance || (isSolanaChain.value && token.balanceStatus === 'unavailable')

  const tokensWithBalanceCount = computed(() => tokens.value.filter(matchesBalanceFilter).length)

  // Compute filtered tokens - preserve canonical ordering from service
  // Service already sorts by priority: USDT > USDC > native > others
  const filteredTokens = computed(() => {
    let result = [...tokens.value]

    // Filter by balance toggle
    if (showOnlyWithBalance.value && hasConnectedWallet.value) {
      result = result.filter(matchesBalanceFilter)
    }

    // Filter by search query
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase()
      result = result.filter(
        (t) =>
          t.symbol.toLowerCase().includes(query) ||
          t.name.toLowerCase().includes(query) ||
          t.address.toLowerCase() === query
      )
    }

    // Note: We intentionally do NOT re-sort here.
    // Tokens come pre-sorted from the service layer with priority ranking.

    return result
  })

  const isSelected = (token: WalletSourceToken) => {
    if (!selectedAddress.value) return false
    return normalizeAddrForCompare(token.address) === normalizeAddrForCompare(selectedAddress.value)
  }

  const selectToken = (token: WalletSourceToken) => {
    emit(token)
    searchQuery.value = ''
  }

  const formatBalance = (balance: string): string => formatTokenBalance(balance)

  return {
    isSolanaChain,
    searchQuery,
    showOnlyWithBalance,
    selectedToken,
    tokensWithBalanceCount,
    filteredTokens,
    isSelected,
    selectToken,
    formatBalance,
  }
}
